<template>
  <div id="app">
    <error-boundary>
      <app-loader />
      <app-themes>
        <system-warning-banner v-if="isCert()" />
        <notifications classes="custom_notification" group="error" position="bottom right" />
        <app-header v-if="showHeader" :isDemo="isDemo" />
        <main>
          <div
            class="hidden-md bg-gradient-default"
            style="
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: white;
              display: flex;
              justify-content: center;
              align-items: center;
            "
          >
            <div class="iconContainer">
              <svg
                class="phone-icon"
                width="80"
                height="80"
                viewBox="0 0 80 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <!-- Phone body -->
                <rect x="25" y="10" width="30" height="60" rx="4" fill="#000" stroke="#000" stroke-width="2"></rect>

                <!-- Screen -->
                <rect x="28" y="16" width="24" height="48" rx="2" fill="#efefef"></rect>

                <!-- Home button -->
                <circle cx="40" cy="66" r="2" fill="#efefef"></circle>

                <!-- Camera -->
                <circle cx="40" cy="13" r="1.5" fill="#efefef"></circle>

                <!-- Rotation arrows -->
                <g class="arrows">
                  <!-- Right arrow -->
                  <path
                    d="M58 35 L63 40 L58 45"
                    stroke="#666"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                  ></path>
                  <path
                    d="M63 40 Q58 40 55 42"
                    stroke="#666"
                    stroke-width="2"
                    stroke-linecap="round"
                    fill="none"
                  ></path>

                  <!-- Left arrow -->
                  <path
                    d="M22 45 L17 40 L22 35"
                    stroke="#666"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                  ></path>
                  <path
                    d="M17 40 Q22 40 25 38"
                    stroke="#666"
                    stroke-width="2"
                    stroke-linecap="round"
                    fill="none"
                  ></path>
                </g>
              </svg>
              <h3>Rotate Your Device</h3>
              <p>For the best experience, please rotate your phone to landscape mode (horizontal)</p>
            </div>
          </div>
          <transition name="fade" mode="out-in" appear class="default-transition">
            <router-view class="hidden-sm" />
          </transition>
        </main>
        <app-footer />
        <vue-progress-bar></vue-progress-bar>
      </app-themes>
    </error-boundary>
    <component v-if="mathjaxUrl" :src="mathjaxUrl" :is="'script'"></component>
  </div>
</template>
<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter, State } from 'vuex-class';
import ErrorBoundary from '@/components/common/ErrorBoundary.vue';
import AppHeader from '@/layout/AppHeader.vue';
import AppFooter from '@/layout/AppFooter.vue';
import AppThemes from '@/AppThemes.vue';
import AppLoader from '@/layout/AppLoader.vue';
import SystemWarningBanner from '@/components/SystemWarningBanner.vue';
import { isCert } from '@/helpers';
import type { ThemeConfig } from '@/plugins/config';
import type { IUser } from '@/store/modules/auth/types';

@Component({
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    ErrorBoundary,
    AppThemes,
    AppLoader,
    SystemWarningBanner,
  },
  methods: { isCert },
})
export default class App extends Vue {
  public currentPath: string = '/';
  public isDemo: boolean = false;
  public hasHeader: boolean = false;
  public isLogin: boolean = false;

  @State('isCompleted', { namespace: 'authModule' })
  private isCompleted!: boolean;

  @State('isLogged', { namespace: 'authModule' })
  private isLogged!: boolean;

  @Getter('theme')
  public theme!: ThemeConfig;

  @Getter('replaceAppHeader')
  public replaceAppHeader!: boolean;

  @Getter('account', { namespace: 'authModule' })
  public account!: IUser;

  @Action('setProduct')
  public setProduct!: (payload: string) => Promise<void>;

  @Watch('account', { immediate: true, deep: true })
  public onAccountChanged(val: IUser): void {
    if (val && val.activeProduct) {
      this.setProduct(val.activeProduct.product_type.toLowerCase());
    }
  }

  get showHeader() {
    return (this.isLogged && this.isCompleted && !this.replaceAppHeader) || this.hasHeader;
  }

  get isMobile() {
    return window.innerWidth < 840;
  }

  get mathjaxUrl() {
    return '/mathjax/es5/tex-mml-chtml.js';
  }

  @Watch('$route.path', { immediate: true })
  public changeRoute(path: string): void {
    this.currentPath = path;
    this.isLogin = path === '/' || path === '/login' || path === '/forgot' || path === '/sso';
  }

  @Watch('$route.matched', { immediate: true })
  public changeRouteMatched(path: any): void {
    if (path.length) {
      this.hasHeader =
        (path[0]['name'] === 'practice' && (path.length > 1 && path[1]['name']) !== 'practice-login') ||
        path[0]['name'] === 'about';

      this.isDemo = path[0]['name'] === 'practice' && path[1]['name'] !== 'practice-login';
    }
  }
}
</script>

<style>
.fixed-top {
  top: var(--system-banner-height, 0px) !important;
}

body {
  padding-top: var(--system-banner-height, 0px);
}

@media (min-width: 840px) {
  .hidden-md {
    display: none !important;
  }
}

@media (min-width: 768px) and (max-width: 1000px) {
  .container {
    max-width: 100% !important;
  }
}

@media (max-width: 840px) {
  .hidden-sm {
    display: none !important;
  }
}

.iconContainer {
  width: 390px;
  height: 250px;
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -195px;
  margin-top: -195px;
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  max-width: 400px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.phone-icon {
  animation: rotate 2s ease-in-out infinite;
  transform-origin: center;
}

/* SVG Phone Icon */
@keyframes rotate {
  0%,
  30% {
    transform: rotate(0deg);
  }
  60%,
  100% {
    transform: rotate(90deg);
  }
}
</style>
